import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";

import {
  fetchMedications,
  fetchMedicationById,
  searchMedications,
  fetchMedicationsStats,
} from "../redux/thunks/medicationsDatabaseThunks";

import {
  selectMedications,
  selectCurrentMedication,
  selectSearchResults,
  selectMedicationsStats,
  selectPagination,
  selectMedicationsLoading,
  selectSearchLoading,
  selectStatsLoading,
  selectMedicationsError,
  selectSearchError,
  selectHasMedications,
  selectHasSearchResults,
} from "../redux/selectors/medicationsDatabaseSelectors";

import {
  clearCurrentMedication,
  clearSearchResults,
  clearError,
} from "../redux/slices/medicationsDatabaseSlice";

/**
 * Hook pour consommer l'API de la base de données des médicaments
 * Permet de rechercher, lister et récupérer les détails des médicaments
 */
export default function useMedicationsDatabase() {
  const dispatch = useDispatch();

  // Sélecteurs Redux
  const medications = useSelector(selectMedications);
  const currentMedication = useSelector(selectCurrentMedication);
  const searchResults = useSelector(selectSearchResults);
  const stats = useSelector(selectMedicationsStats);
  const pagination = useSelector(selectPagination);
  
  // États de chargement
  const loading = useSelector(selectMedicationsLoading);
  const searchLoading = useSelector(selectSearchLoading);
  const statsLoading = useSelector(selectStatsLoading);
  
  // Erreurs
  const error = useSelector(selectMedicationsError);
  const searchError = useSelector(selectSearchError);
  
  // Helpers
  const hasMedications = useSelector(selectHasMedications);
  const hasSearchResults = useSelector(selectHasSearchResults);

  /**
   * 📡 Récupérer la liste des médicamentsavec pagination
   */
  const loadMedications = useCallback(
    async ({ page = 1, limit = 20 } = {}) => {
      try {
        const res = await dispatch(fetchMedications({ page, limit })).unwrap();
        return res;
      } catch (err) {
        const msg = err?.message || "Erreur lors du chargement des médicaments";
        message.error(msg);
        throw err;
      }
    },
    [dispatch]
  );

  /**
   * 🔍 Rechercher des médicaments
   * @param {Object} params - Paramètres de recherche
   * @param {string} params.q - Recherche textuelle
   * @param {string} params.dci - Dénomination Commune Internationale
   * @param {string} params.laboratoire - Nom du laboratoire
   * @param {string} params.classeTherapeutique - Classe thérapeutique
   * @param {number} params.page - Numéro de page
   * @param {number} params.limit - Limite par page
   */
  const search = useCallback(
    async ({
      q,
      dci,
      laboratoire,
      classeTherapeutique,
      page = 1,
      limit = 20,
    } = {}) => {
      
      try {
        const res = await dispatch(
          searchMedications({
            q,
            dci,
            laboratoire,
            classeTherapeutique,
            page,
            limit,
          })
        ).unwrap();
        
        return res;
      } catch (err) {
        const msg = err?.message || "Erreur lors de la recherche";
        message.error(msg);
        throw err;
      }
    },
    [dispatch]
  );

  /**
   * 📄 Récupérer un médicament par son ID
   */
  const loadMedicationById = useCallback(
    async (medicationId) => {
      if (!medicationId) {
        return;
      }
      
      
      try {
        const res = await dispatch(
          fetchMedicationById({ medicationId })
        ).unwrap();
        
        return res;
      } catch (err) {
        const msg = err?.message || "Erreur lors du chargement du médicament";
        message.error(msg);
        throw err;
      }
    },
    [dispatch]
  );

  /**
   * 📊 Récupérer les statistiques
   */
  const loadStats = useCallback(async () => {
    
    try {
      const res = await dispatch(fetchMedicationsStats()).unwrap();
      return res;
    } catch (err) {
      const msg = err?.message || "Erreur lors du chargement des statistiques";
      message.error(msg);
      throw err;
    }
  }, [dispatch]);

  /**
   * 🧹 Réinitialiser le médicament actuel
   */
  const clearCurrent = useCallback(() => {
    dispatch(clearCurrentMedication());
  }, [dispatch]);

  /**
   * 🧹 Effacer les résultats de recherche
   */
  const clearSearch = useCallback(() => {
    dispatch(clearSearchResults());
  }, [dispatch]);

  /**
   * 🧹 Effacer toutes les erreurs
   */
  const clearErrors = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    // Données
    medications,
    currentMedication,
    searchResults,
    stats,
    pagination,
    
    // États
    loading,
    searchLoading,
    statsLoading,
    isLoading: loading || searchLoading || statsLoading,
    
    // Erreurs
    error,
    searchError,
    
    // Helpers
    hasMedications,
    hasSearchResults,
    
    // Actions
    loadMedications,
    search,
    loadMedicationById,
    loadStats,
    
    // Nettoyage
    clearCurrent,
    clearSearch,
    clearErrors,
  };
}
